import { formatTimer } from "@utils/misc";
import { Button, ButtonClasses, cn } from "kz-ui-sdk";
import { forwardRef, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useOTPCountDown } from "../FormOTP/hooks/useOTPCountDown";
import styles from "./GetCodeButton.module.css";

interface GetCodeButtonProps extends ButtonClasses {
  onGetCode: () => Promise<any>;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  pulse?: boolean;
}

export interface GetCodeButtonRef {
  startTimer: (remainingSeconds: number) => void;
}

const GetCodeButton = forwardRef<GetCodeButtonRef, GetCodeButtonProps>(
  (
    { onGetCode, disabled = false, className = "", classes, loading = false, pulse = false }: GetCodeButtonProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const { time: countdown, countFrom } = useOTPCountDown();

    useImperativeHandle(ref, () => ({
      startTimer: (remainingSeconds: number) => {
        countFrom(remainingSeconds);
      },
    }));

    const handleGetCode = useCallback(
      async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onGetCode().catch(() => {});
      },
      [onGetCode],
    );

    const formatCountdown = (seconds: number) => {
      return t("resend in ({{seconds}})", { seconds: formatTimer(seconds, { minutes: false, seconds: true }) });
    };

    return (
      <Button
        variant="primary"
        size="sm"
        className={cn(className, pulse ? styles.getCodeHint : undefined)}
        onClick={handleGetCode}
        disabled={disabled || countdown > 0 || loading}
        classes={classes}
        loading={loading}
        type="button"
        aria-label="Get code"
        data-element-id="button-get-code"
      >
        {countdown > 0 ? formatCountdown(countdown) : t("get code")}
      </Button>
    );
  },
);

GetCodeButton.displayName = "GetCodeButton";

export default GetCodeButton;
